import { useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { testTypeSchema } from "../../yup";
import { useCreateTestTypeMutation, useGetSpecialitiesQuery } from "../../services/api";
import style from "./testType.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import TestTypeFormFields from "./TestTypeFormFields";
import type { ApiError, SpecialityOption, TestTypeFormValues } from "./testTypeTypes";
import type { Speciality } from "../../types/index";

type FormState = TestTypeFormValues;

/**
 * Renders the Add Test Type form.
 *
 * The form collects test type details, validates with the schema, and submits
 * a create mutation on success.
 *
 * Returns:
 *   JSX.Element: The Add Test Type page content.
 */
const AddTestType = () => {
  const [createTestType] = useCreateTestTypeMutation();

  const { data: specialityData = { items: [] } } = useGetSpecialitiesQuery({
    order_by_columns: "name",
    order: "Asc",
    is_detailed: true,
    search_key: ""
  });

  const specialities = useMemo<SpecialityOption[]>(
    () => specialityData?.items?.map((speciality: Speciality) => ({
      id: speciality?.id,
      label: speciality?.name,
      value: speciality?.name,
    })) ?? [],
    [specialityData]
  );
  const navigate = useNavigate();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(testTypeSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    watch,
    setValue
  } = useForm<FormState>({
    resolver: yupResolver<TestTypeFormValues>(testTypeSchema),
    mode: "onChange",
  });

  const onSubmit = async (values: FormState) => {
    try {
      setIsLoading(true);
      const { name, speciality_ids, is_intra_operative, info, specimen_storage } = values;
      const payload: AddTestTypePayload = {
        name: name,
        speciality_ids: speciality_ids?.map((speciality) => speciality.id) ?? [],
        is_intra_operative: is_intra_operative ?? false,
        info: info ?? "",
        specimen_storage: specimen_storage
      };

      await createTestType(payload).unwrap();
      setIsLoading(false);
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      const apiError = error as ApiError;
      setMessage({
        type: "error",
        text: apiError?.data?.detail?.toString() || "Please enter valid details",
      });
      setIsLoading(false);
    }
  };

  return (
    <Box className={style.container}>
      <Typography className={style.heading}>Add Test Type</Typography>
      <Typography className={style.description}>
        Enter the below details to start adding a new test type to Pegasus
        Cybersoft.
      </Typography>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <Box className={style.flexTop}>
          <TestTypeFormFields
            register={register}
            errors={errors}
            control={control}
            getValues={getValues}
            watch={watch}
            setValue={setValue}
            specialities={specialities}
          />
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        <Box className={style.buttons}>
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: "transparent" },
            }}
            variant="outlined"
            onClick={() => navigate(-1)}
            label={"Cancel"}
          />
          <Button
            sx={{
              width: "200px",
              height: "55px",
              ":hover": { backgroundColor: colors.primary },
            }}
            variant="contained"
            type="submit"
            label={"Save"}
            loading={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
}

export default AddTestType;
